(() => {
  const ROOT = '[data-phc-native-schedule]';
  const DAY_MS = 86400000;
  const PHC_TIME_ZONE = 'Europe/Copenhagen';
  const cache = new Map();

  const qs = (root, selector) => root.querySelector(selector);
  const qsa = (root, selector) => Array.from(root.querySelectorAll(selector));
  const text = (value) => String(value ?? '').trim();
  const first = (...values) => values.find((value) => value !== undefined && value !== null && text(value) !== '');
  const asArray = (value) => Array.isArray(value) ? value : (value ? [value] : []);
  const safeJson = async (response) => {
    const raw = await response.text();
    try { return raw ? JSON.parse(raw) : {}; } catch (_) { throw new Error(`Invalid JSON response (${response.status})`); }
  };
  const htmlEscape = (value) => text(value).replace(/[&<>'"]/g, (char) => ({'&':'&amp;','<':'&lt;','>':'&gt;',"'":'&#39;','"':'&quot;'}[char]));
  const FACILITATOR_IDENTITY_STORAGE_KEY = 'phc:facilitator-identities:v1';
  const FACILITATOR_IDENTITY_TTL = 30 * 60 * 1000;
  const facilitatorPathKey = (value = window.location.href) => {
    try {
      const url = new URL(value, window.location.href);
      const path = url.pathname.replace(/\/+$/, '') || '/';
      return path.toLowerCase();
    } catch (_) {
      return (window.location.pathname.replace(/\/+$/, '') || '/').toLowerCase();
    }
  };
  const readFacilitatorIdentityStore = () => {
    try {
      const parsed = JSON.parse(sessionStorage.getItem(FACILITATOR_IDENTITY_STORAGE_KEY) || '{}');
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch (_) {
      return {};
    }
  };
  const rememberFacilitatorIdentity = ({ url = window.location.href, publicProfileId = '', employeeId = '', name = '' } = {}) => {
    const profileId = text(publicProfileId);
    const employee = text(employeeId);
    if (!profileId && !employee) return;
    try {
      const store = readFacilitatorIdentityStore();
      const now = Date.now();
      Object.keys(store).forEach((key) => {
        if (!store[key] || now - Number(store[key].savedAt || 0) > FACILITATOR_IDENTITY_TTL) delete store[key];
      });
      store[facilitatorPathKey(url)] = {
        publicProfileId: profileId,
        employeeId: employee,
        name: text(name),
        savedAt: now
      };
      const entries = Object.entries(store).sort((a, b) => Number(b[1]?.savedAt || 0) - Number(a[1]?.savedAt || 0)).slice(0, 24);
      sessionStorage.setItem(FACILITATOR_IDENTITY_STORAGE_KEY, JSON.stringify(Object.fromEntries(entries)));
    } catch (_) {}
  };
  const readRememberedFacilitatorIdentity = (url = window.location.href) => {
    const store = readFacilitatorIdentityStore();
    const row = store[facilitatorPathKey(url)];
    if (!row || Date.now() - Number(row.savedAt || 0) > FACILITATOR_IDENTITY_TTL) return null;
    return row;
  };

  const copenhagenDateParts = (date) => {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: PHC_TIME_ZONE,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hourCycle: 'h23'
    }).formatToParts(date).reduce((acc, part) => {
      if (part.type !== 'literal') acc[part.type] = part.value;
      return acc;
    }, {});
    // Some WebKit/ICU combinations have historically surfaced midnight as 24
    // even when h23 was requested. Treat that as 00 of the returned calendar
    // date so Copenhagen offset math cannot shift a session onto another day.
    const rawHour = Number(parts.hour);
    return {
      year: Number(parts.year), month: Number(parts.month), day: Number(parts.day),
      hour: rawHour === 24 ? 0 : rawHour, minute: Number(parts.minute), second: Number(parts.second)
    };
  };
  const copenhagenOffsetMs = (date) => {
    const parts = copenhagenDateParts(date);
    return Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second) - date.getTime();
  };
  const copenhagenDate = (year, month, day, hour = 0, minute = 0, second = 0) => {
    const localUtc = Date.UTC(year, month - 1, day, hour, minute, second);
    let candidate = new Date(localUtc);
    let offset = copenhagenOffsetMs(candidate);
    candidate = new Date(localUtc - offset);
    const correctedOffset = copenhagenOffsetMs(candidate);
    if (correctedOffset !== offset) candidate = new Date(localUtc - correctedOffset);
    return candidate;
  };
  const localIso = (date) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
    // Date-only formatting deliberately does not share the hour formatter used
    // for timezone-offset calculations. This keeps the calendar key stable on
    // iOS Safari as well as Chromium/Firefox.
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: PHC_TIME_ZONE, year: 'numeric', month: '2-digit', day: '2-digit'
    }).formatToParts(date).reduce((acc, part) => {
      if (part.type !== 'literal') acc[part.type] = part.value;
      return acc;
    }, {});
    return `${parts.year}-${parts.month}-${parts.day}`;
  };
  const dateFromIso = (iso, hour = 0, minute = 0, second = 0) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text(iso));
    return match ? copenhagenDate(Number(match[1]), Number(match[2]), Number(match[3]), hour, minute, second) : null;
  };
  const shiftIsoDate = (iso, days) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text(iso));
    if (!match) return iso;
    const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]) + days, 12));
    return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;
  };
  const PHC_WEEKDAYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
  const PHC_MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
  const isoDisplayParts = (iso) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text(iso));
    if (!match) return null;
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    if (!Number.isInteger(year) || month < 1 || month > 12 || day < 1 || day > 31) return null;
    const weekday = new Date(Date.UTC(year, month - 1, day, 12)).getUTCDay();
    return { year, month, day, weekday: PHC_WEEKDAYS[weekday], monthShort: PHC_MONTHS[month - 1] };
  };
  const diffIsoDays = (fromIso, toIso) => {
    const from = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text(fromIso));
    const to = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text(toIso));
    if (!from || !to) return 0;
    const fromDay = Date.UTC(Number(from[1]), Number(from[2]) - 1, Number(from[3]));
    const toDay = Date.UTC(Number(to[1]), Number(to[2]) - 1, Number(to[3]));
    return Math.round((toDay - fromDay) / DAY_MS);
  };
  const parseDate = (value, dateOnly = '') => {
    if (!value && !dateOnly) return null;
    let raw = text(value || dateOnly);
    if (dateOnly && /^\d{1,2}:\d{2}/.test(raw)) raw = `${dateOnly}T${raw}`;

    const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
    if (dateOnlyMatch) {
      return copenhagenDate(Number(dateOnlyMatch[1]), Number(dateOnlyMatch[2]), Number(dateOnlyMatch[3]));
    }

    // Parse Mariana timestamps ourselves instead of delegating to Date.parse.
    // WebKit/Safari and Chromium do not accept exactly the same non-canonical
    // ISO variants (space separator, fractional precision, etc.). A booking
    // calendar must therefore not let the browser engine decide which sessions
    // exist on a given date.
    const timestamp = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{1,2}):(\d{2})(?::(\d{2})(?:\.(\d{1,9}))?)?(Z|[+-]\d{2}:?\d{2})?$/i.exec(raw);
    if (timestamp) {
      const year = Number(timestamp[1]);
      const month = Number(timestamp[2]);
      const day = Number(timestamp[3]);
      const hour = Number(timestamp[4]);
      const minute = Number(timestamp[5]);
      const second = Number(timestamp[6] || 0);
      const millisecond = Number(String(timestamp[7] || '').slice(0, 3).padEnd(3, '0') || 0);
      const zone = text(timestamp[8]);

      if (!zone) {
        // Timezone-less Mariana values are studio-local Copenhagen time.
        const local = copenhagenDate(year, month, day, hour, minute, second);
        return Number.isNaN(local.getTime()) ? null : new Date(local.getTime() + millisecond);
      }

      let offsetMinutes = 0;
      if (zone.toUpperCase() !== 'Z') {
        const offset = /^([+-])(\d{2}):?(\d{2})$/.exec(zone);
        if (!offset) return null;
        const direction = offset[1] === '+' ? 1 : -1;
        offsetMinutes = direction * (Number(offset[2]) * 60 + Number(offset[3]));
      }
      const utc = Date.UTC(year, month - 1, day, hour, minute, second, millisecond) - offsetMinutes * 60000;
      const parsed = new Date(utc);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    // Do not silently let one browser parse an unknown Mariana format while
    // another browser rejects it. Unknown formats are excluded consistently.
    return null;
  };
  const dateOnlyFromAttrs = (attrs) => text(first(
    attrs.start_date, attrs.startDate, attrs.date, attrs.session_date, attrs.sessionDate, ''
  ));
  const startDateFromAttrs = (attrs) => parseDate(first(
    attrs.start_datetime, attrs.startDateTime, attrs.starts_at, attrs.startsAt, attrs.start_at, attrs.startAt,
    attrs.datetime, attrs.date_time, attrs.dateTime, attrs.start_time, attrs.startTime
  ), dateOnlyFromAttrs(attrs));
  const endDateFromAttrs = (attrs, start) => {
    const baseDate = dateOnlyFromAttrs(attrs) || (start ? localIso(start) : '');
    const parsed = parseDate(first(
      attrs.end_datetime, attrs.endDateTime, attrs.end_date_time, attrs.endDateTime,
      attrs.ends_at, attrs.endsAt, attrs.end_at, attrs.endAt, attrs.end_time, attrs.endTime, attrs.end
    ), baseDate);
    if (parsed) {
      // Time-only end values belong to the same Copenhagen session date. If a
      // tenant ever returns an overnight class, roll an end earlier than the
      // start into the following day rather than keeping a stale session alive.
      if (start && parsed.getTime() < start.getTime() && text(first(attrs.end_time, attrs.endTime, '')).match(/^\d{1,2}:\d{2}/)) {
        return new Date(parsed.getTime() + DAY_MS);
      }
      return parsed;
    }
    const duration = Number(first(attrs.duration_minutes, attrs.durationMinutes, attrs.duration, 0));
    return start && duration > 0 ? new Date(start.getTime() + duration * 60000) : null;
  };

  const attrsOf = (resource) => ({ ...(resource || {}), ...((resource?.attributes && typeof resource.attributes === 'object') ? resource.attributes : {}) });
  const includedIndex = (payload) => {
    const index = new Map();
    asArray(payload?.included).forEach((resource) => {
      if (resource?.type && resource?.id != null) index.set(`${resource.type}:${resource.id}`, resource);
    });
    return index;
  };
  const relationshipRefs = (resource, names) => {
    const rels = resource?.relationships || {};
    const refs = [];
    names.forEach((name) => {
      const data = rels?.[name]?.data;
      asArray(data).forEach((item) => { if (item?.id != null) refs.push(item); });
    });
    return refs;
  };
  const relationshipResources = (resource, names, index) => relationshipRefs(resource, names).map((ref) => index.get(`${ref.type}:${ref.id}`) || ref);
  const labelFrom = (resource) => {
    const attrs = attrsOf(resource);
    return text(first(attrs.schedule_display_name, attrs.scheduleDisplayName, attrs.display_name, attrs.displayName, attrs.name, attrs.title, attrs.label, attrs.profile_name, attrs.profileName, ''));
  };
  const idFromRel = (resource, names) => relationshipRefs(resource, names)[0]?.id ? String(relationshipRefs(resource, names)[0].id) : '';

  const canonicalRitual = (label) => {
    const raw = text(label);
    const lower = raw.toLowerCase();
    if (/(^|\W)deep(\W|$)/.test(lower)) return 'Deep';
    if (/(^|\W)club(\W|$)/.test(lower)) return 'Club';
    if (/(^|\W)peak(\W|$)/.test(lower)) return 'Peak';
    return raw;
  };

  // Availability is normalized server-side from Mariana's customer-facing
  // class feed and explicit session fields. Never recursively guess values from
  // unrelated resources, and never coerce null/empty values to zero.
  const strictFiniteNumber = (value) => {
    if (value === null || value === undefined || value === '' || typeof value === 'boolean') return NaN;
    if (typeof value === 'string' && value.trim() === '') return NaN;
    const number = typeof value === 'number' ? value : Number(String(value).trim());
    return Number.isFinite(number) ? number : NaN;
  };
  const normalizedAvailability = (resource) => {
    const raw = resource?.phc_availability;
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
      return { spots: NaN, capacity: NaN, explicitFull: false, waitlistAvailable: false, hasExplicitFull: false };
    }
    let spots = strictFiniteNumber(raw.available);
    let capacity = strictFiniteNumber(raw.capacity);
    if (Number.isFinite(spots)) spots = Math.max(0, Math.floor(spots));
    if (Number.isFinite(capacity)) capacity = Math.max(0, Math.floor(capacity));

    // A mismatched total is not trustworthy enough for an x / y claim. Keep the
    // live available count and fall back to `X SEATS LEFT` instead.
    if (Number.isFinite(spots) && Number.isFinite(capacity) && (capacity <= 0 || spots > capacity)) {
      capacity = NaN;
    }

    return {
      spots,
      capacity,
      explicitFull: raw.is_full === true,
      hasExplicitFull: raw.is_full === true || raw.is_full === false,
      waitlistAvailable: raw.waitlist_available === true
    };
  };

  const normalizeSession = (resource, payload, directories = {}) => {
    const attrs = attrsOf(resource);
    const index = directories.index || includedIndex(payload);
    const start = startDateFromAttrs(attrs);
    const end = endDateFromAttrs(attrs, start);

    const typeResource = relationshipResources(resource, ['class_session_type', 'class_type', 'session_type'], index)[0];
    const typeId = idFromRel(resource, ['class_session_type', 'class_type', 'session_type']);
    const typeLabel = text(first(
      attrs.class_session_type_name, attrs.classSessionTypeName, attrs.class_type_name, attrs.classTypeName,
      attrs.session_type_name, attrs.sessionTypeName, attrs.class_name, attrs.className,
      labelFrom(typeResource), directories.types?.get(typeId), attrs.name, attrs.title, 'Session'
    ));

    const locationResource = relationshipResources(resource, ['location', 'studio', 'venue'], index)[0];
    const locationId = idFromRel(resource, ['location', 'studio', 'venue']);
    const locationLabel = text(first(attrs.location_name, attrs.locationName, labelFrom(locationResource), directories.locations?.get(locationId), ''));

    const classroomResource = relationshipResources(resource, ['classroom', 'room'], index)[0];
    const classroom = text(first(attrs.classroom_name, attrs.classroomName, labelFrom(classroomResource), ''));
    const layoutResource = relationshipResources(resource, ['layout', 'classroom_layout', 'classLayout', 'class_layout'], index)[0];

    const instructorRelationshipNames = [
      'employee_public_profiles', 'employee_public_profile', 'instructors', 'instructor_profiles',
      'primary_instructor', 'primary_instructors', 'substitute_instructor', 'substitute_instructors',
      'facilitators', 'teachers', 'employees', 'employee', 'instructor_employees'
    ];
    const instructorResources = relationshipResources(resource, instructorRelationshipNames, index);
    const instructorRefs = relationshipRefs(resource, instructorRelationshipNames);
    const instructors = instructorResources.map((item, idx) => {
      const itemAttrs = attrsOf(item);
      const ref = instructorRefs[idx] || {};
      const itemId = text(first(item?.id, ref?.id, ''));
      const itemType = text(first(item?.type, ref?.type, '')).toLowerCase();
      const publicProfileRel = relationshipRefs(item, ['employee_public_profile', 'employee_public_profiles', 'public_profile', 'public_profiles'])[0];
      const employeeRel = relationshipRefs(item, ['employee', 'employees'])[0];
      const publicProfileId = text(first(
        itemAttrs.employee_public_profile_id, itemAttrs.employeePublicProfileId,
        itemAttrs.public_profile_id, itemAttrs.publicProfileId,
        publicProfileRel?.id,
        /employee[_-]?public[_-]?profiles?/.test(itemType) ? itemId : '',
        ''
      ));
      const employeeId = text(first(
        itemAttrs.employee_id, itemAttrs.employeeId,
        employeeRel?.id,
        /^employees?$/.test(itemType) ? itemId : '',
        ''
      ));
      return {
        id: itemId,
        publicProfileId,
        employeeId,
        name: labelFrom(item)
      };
    }).filter((item) => item.id || item.publicProfileId || item.employeeId || item.name);

    // Mariana can expose instructor identity directly on class-session attributes.
    // Keep these direct IDs in the same normalized instructor list so facilitator
    // pages can match without a second schedule request.
    const appendAttributeInstructorIds = (keys, field) => {
      keys.forEach((key) => {
        const raw = attrs[key];
        const values = Array.isArray(raw) ? raw : (typeof raw === 'string' ? raw.split(/[,|;\s]+/) : []);
        values.map(text).filter(Boolean).forEach((id) => {
          if (instructors.some((person) => text(person?.[field]) === id || text(person?.id) === id)) return;
          instructors.push({
            id,
            publicProfileId: field === 'publicProfileId' ? id : '',
            employeeId: field === 'employeeId' ? id : '',
            name: ''
          });
        });
      });
    };
    appendAttributeInstructorIds(['employee_public_profile_ids', 'employeePublicProfileIds', 'instructor_profile_ids', 'instructorProfileIds'], 'publicProfileId');
    appendAttributeInstructorIds(['employee_ids', 'employeeIds', 'instructor_employee_ids', 'instructorEmployeeIds'], 'employeeId');

    const availability = normalizedAvailability(resource);
    const spots = availability.spots;
    const capacity = availability.capacity;
    const isFull = availability.explicitFull || (Number.isFinite(spots) && spots === 0);
    const waitlistAvailable = availability.waitlistAvailable;
    const cancelled = attrs.cancelled === true || attrs.canceled === true || attrs.is_cancelled === true || attrs.isCanceled === true || /cancel/.test(text(attrs.status).toLowerCase());
    const bookable = !cancelled && attrs.bookable !== false && attrs.can_book !== false && attrs.canBook !== false;
    const seatsLabel = Number.isFinite(spots) && spots > 0 && Number.isFinite(capacity) && capacity > 0
      ? `${spots} / ${capacity} SEATS AVAILABLE`
      : Number.isFinite(spots) && spots > 0
        ? `${spots} SEAT${spots === 1 ? '' : 'S'} LEFT`
        : 'CHECK AVAILABILITY';
    let availabilityState = 'available';
    let availabilityLabel = seatsLabel;
    if (cancelled) { availabilityState = 'cancelled'; availabilityLabel = 'CANCELLED'; }
    else if (isFull && waitlistAvailable) { availabilityState = 'waitlist'; availabilityLabel = 'WAITLIST AVAILABLE'; }
    else if (isFull) { availabilityState = 'full'; availabilityLabel = 'FULL'; }
    else if (Number.isFinite(spots) && spots <= 4) { availabilityState = 'limited'; availabilityLabel = seatsLabel; }

    return {
      id: text(resource?.id),
      name: typeLabel || 'Session',
      ritual: canonicalRitual(typeLabel),
      typeId,
      start,
      end,
      // Mariana can expose the local calendar day as start_date, date or
      // session_date depending on the class-session response shape. Use that
      // explicit YYYY-MM-DD value as the calendar key; otherwise derive the
      // Europe/Copenhagen day from the parsed start instant.
      date: /^\d{4}-\d{2}-\d{2}$/.test(dateOnlyFromAttrs(attrs)) ? dateOnlyFromAttrs(attrs) : (start ? localIso(start) : ''),
      locationId,
      location: locationLabel,
      classroom,
      instructors,
      description: text(first(attrs.description, attrs.long_description, attrs.longDescription, attrs.notes, typeResource ? attrsOf(typeResource).description : '', '')),
      capacity: Number.isFinite(capacity) ? capacity : null,
      spots: Number.isFinite(spots) ? spots : null,
      availabilityState,
      availabilityLabel,
      bookable,
      waitlistAvailable,
      raw: resource
    };
  };

  const absoluteUrl = (value) => /^https?:\/\//i.test(text(value)) ? new URL(text(value)) : new URL(text(value), window.location.origin);

  const buildProxyUrl = (proxy, action, params = {}) => {
    const url = absoluteUrl(proxy);
    url.search = '';
    url.searchParams.set('action', action);
    Object.entries(params).forEach(([key, value]) => {
      const normalized = text(value);
      if (normalized) url.searchParams.set(key, normalized);
    });
    return url;
  };

  const fetchThroughGenericProxy = async (proxy, apiBase, path, params = {}) => {
    const upstream = new URL(`${apiBase.replace(/\/$/, '')}/${path.replace(/^\//, '')}`);
    Object.entries(params).forEach(([key, value]) => {
      const normalized = text(value);
      if (normalized) upstream.searchParams.set(key, normalized);
    });
    const url = absoluteUrl(proxy);
    url.search = '';
    url.searchParams.set('url', upstream.toString());
    const response = await fetch(url, { headers: { Accept: 'application/json' }, credentials: 'omit' });
    const payload = await safeJson(response);
    if (!response.ok) throw new Error(payload?.error || `Mariana proxy returned ${response.status}`);
    return payload;
  };

  const fetchSchedule = async (root, options = {}) => {
    const proxy = text(root.dataset.phcNativeProxyUrl);
    const apiBase = text(root.dataset.phcNativeApiBase) || 'https://pluginheatclub.marianatek.com/api';
    if (!proxy) throw new Error('Mariana proxy URL is not configured');

    const todayIso = localIso(new Date());
    const configuredDays = Number(options.days || root.dataset.phcNativeDays || 14);
    const days = Math.min(180, Math.max(7, configuredDays));
    const locationId = text(root.dataset.phcNativeLocationId);
    const facilitatorMode = root.dataset.phcNativeMode === 'facilitator';
    const profileId = text(root.dataset.phcNativeFacilitatorProfileId);
    const params = { min_date: todayIso, max_date: shiftIsoDate(todayIso, days - 1), location: locationId, page_size: '250' };
    // Facilitator pages are scoped server-side with a verified Mariana public
    // profile id. Do not fetch every facilitator and then guess identity locally.
    if (facilitatorMode && profileId) params.employee_public_profiles = profileId;
    const cacheKey = `${proxy}|${apiBase}|${JSON.stringify(params)}`;
    if (cache.has(cacheKey)) return cache.get(cacheKey);

    const promise = (async () => {
      try {
        const response = await fetch(buildProxyUrl(proxy, 'schedule', params), { headers: { Accept: 'application/json' }, credentials: 'omit' });
        const payload = await safeJson(response);
        if (response.ok && Array.isArray(payload?.sessions)) return payload;
        if (response.ok && Array.isArray(payload?.data)) return { sessions: payload.data, raw: payload };
      } catch (_) {
        // v68 proxy fallback below.
      }

      let payload;
      try {
        payload = await fetchThroughGenericProxy(proxy, apiBase, 'class_sessions', { ...params, include: 'class_session_type,employee_public_profiles,location,classroom' });
      } catch (_) {
        payload = await fetchThroughGenericProxy(proxy, apiBase, 'class_sessions', params);
      }
      return { sessions: asArray(payload?.data || payload?.results), raw: payload };
    })();
    cache.set(cacheKey, promise);
    return promise.catch((error) => { cache.delete(cacheKey); throw error; });
  };

  // Merge the later broad response without throwing away fields that were
  // already returned by the first-paint response. In particular, availability
  // enrichment can be present on the first response and absent on a later
  // response if Mariana's customer availability read is temporarily partial.
  const mergeResource = (previous, incoming) => {
    if (!previous || typeof previous !== 'object') return incoming;
    if (!incoming || typeof incoming !== 'object') return previous;
    const merged = {
      ...previous,
      ...incoming,
      attributes: { ...(previous.attributes || {}), ...(incoming.attributes || {}) },
      relationships: { ...(previous.relationships || {}), ...(incoming.relationships || {}) }
    };
    const incomingAvailability = incoming.phc_availability;
    const previousAvailability = previous.phc_availability;
    const previousHasAvailability = previousAvailability && typeof previousAvailability === 'object' && !Array.isArray(previousAvailability);
    const incomingHasAvailability = incomingAvailability && typeof incomingAvailability === 'object' && !Array.isArray(incomingAvailability);
    if (previousHasAvailability || incomingHasAvailability) {
      const availability = { ...(previousHasAvailability ? previousAvailability : {}) };
      if (incomingHasAvailability) {
        ['available', 'capacity'].forEach((key) => {
          const value = incomingAvailability[key];
          if (value !== null && value !== undefined && value !== '' && !Number.isNaN(Number(value))) availability[key] = value;
        });
        ['is_full', 'waitlist_available'].forEach((key) => {
          if (typeof incomingAvailability[key] === 'boolean') availability[key] = incomingAvailability[key];
        });
        ['available_source', 'capacity_source', 'source'].forEach((key) => {
          if (text(incomingAvailability[key])) availability[key] = incomingAvailability[key];
        });
      }
      merged.phc_availability = availability;
    }
    return merged;
  };

  const mergeScheduleResponses = (base, extra) => {
    const sessionMap = new Map();

    // The first-paint range is authoritative for sessions it already returned.
    // Mariana's broader range can be a partial representation of the same class
    // session (for example missing availability or start/date attributes). Never
    // mutate a session the customer can already see with that broader payload:
    // only append genuinely new session ids from the extended horizon.
    asArray(base?.sessions).forEach((session) => {
      if (!session || typeof session !== 'object') return;
      const id = text(session.id);
      const key = id || JSON.stringify(session);
      if (!sessionMap.has(key)) sessionMap.set(key, session);
    });
    asArray(extra?.sessions).forEach((session) => {
      if (!session || typeof session !== 'object') return;
      const id = text(session.id);
      const key = id || JSON.stringify(session);
      if (!sessionMap.has(key)) sessionMap.set(key, session);
    });
    const includedMap = new Map();
    [...asArray(base?.raw?.included), ...asArray(extra?.raw?.included)].forEach((resource) => {
      if (!resource || typeof resource !== 'object') return;
      const type = text(resource.type);
      const id = text(resource.id);
      const key = (type || id) ? `${type}:${id}` : JSON.stringify(resource);
      includedMap.set(key, includedMap.has(key) ? mergeResource(includedMap.get(key), resource) : resource);
    });
    const sessions = Array.from(sessionMap.values());
    return {
      sessions,
      raw: { ...(base?.raw || {}), ...(extra?.raw || {}), data: sessions, included: Array.from(includedMap.values()) },
      meta: { ...(base?.meta || {}), ...(extra?.meta || {}) }
    };
  };

  const deferNonCritical = (callback, timeout = 700) => {
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(callback, { timeout });
    } else {
      window.setTimeout(callback, Math.min(timeout, 250));
    }
  };

  const fetchDirectory = async (root, resource) => {
    const proxy = text(root.dataset.phcNativeProxyUrl);
    const apiBase = text(root.dataset.phcNativeApiBase) || 'https://pluginheatclub.marianatek.com/api';
    const key = `${proxy}|directory|${resource}`;
    if (cache.has(key)) return cache.get(key);
    const promise = fetchThroughGenericProxy(proxy, apiBase, resource, { page_size: '250' }).then((payload) => {
      const map = new Map();
      asArray(payload?.data || payload?.results).forEach((item) => {
        const label = labelFrom(item);
        if (item?.id != null && label) map.set(String(item.id), label);
      });
      return map;
    }).catch(() => new Map());
    cache.set(key, promise);
    return promise;
  };

  const fetchFacilitators = async (root) => {
    const proxy = text(root.dataset.phcNativeProxyUrl);
    if (!proxy) return { byId: new Map(), byName: new Map(), profiles: [] };
    const key = `${proxy}|facilitator-directory`;
    if (cache.has(key)) return cache.get(key);
    const promise = (async () => {
      const response = await fetch(buildProxyUrl(proxy, 'facilitators'), { headers: { Accept: 'application/json' }, credentials: 'omit' });
      const payload = await safeJson(response);
      if (!response.ok) throw new Error(payload?.error || `Facilitator feed returned ${response.status}`);
      const byId = new Map();
      const aliasBuckets = new Map();
      const profiles = asArray(payload?.profiles).map((profile) => {
        const aliases = Array.from(new Set([
          text(profile?.name),
          ...asArray(profile?.match_names).map(text)
        ].filter(Boolean)));
        const normalized = {
          id: text(first(profile?.public_profile_id, profile?.id, '')),
          publicProfileId: text(first(profile?.public_profile_id, profile?.id, '')),
          employeeId: text(profile?.employee_id),
          name: text(profile?.name),
          aliases,
          image: text(first(profile?.thumbnail, profile?.large_image, profile?.image_url, ''))
        };
        [normalized.id, normalized.employeeId].filter(Boolean).forEach((id) => byId.set(id, normalized));
        aliases.forEach((alias) => {
          const keyName = normalizeIdentity(alias);
          if (!keyName) return;
          if (!aliasBuckets.has(keyName)) aliasBuckets.set(keyName, []);
          aliasBuckets.get(keyName).push(normalized);
        });
        return normalized;
      }).filter((profile) => profile.id || profile.employeeId || profile.name);

      // Name/alias fallback is only safe when it resolves to exactly one person.
      // This keeps links deterministic instead of letting the last duplicate win.
      const byName = new Map();
      aliasBuckets.forEach((matches, alias) => {
        const unique = Array.from(new Set(matches));
        if (unique.length === 1) byName.set(alias, unique[0]);
      });
      return { byId, byName, profiles };
    })().catch(() => ({ byId: new Map(), byName: new Map(), profiles: [] }));
    cache.set(key, promise);
    return promise;
  };

  const waitForFacilitatorIdentity = (root, timeoutMs = 7000) => new Promise((resolve) => {
    const section = root.closest?.('[data-phc-mariana-facilitator-schedule]');
    let settled = false;
    let interval = null;
    let timer = null;
    const finish = (value = '') => {
      if (settled) return;
      settled = true;
      window.removeEventListener('phc:facilitator-loaded', handler);
      if (interval) window.clearInterval(interval);
      if (timer) window.clearTimeout(timer);
      resolve(text(value));
    };
    const apply = (profile = {}) => {
      const profileId = text(first(profile?.publicProfileId, profile?.public_profile_id, profile?.id, section?.dataset?.marianaResolvedProfileId, root.dataset.phcNativeFacilitatorProfileId, ''));
      const employeeId = text(first(profile?.employeeId, profile?.employee_id, section?.dataset?.marianaResolvedEmployeeId, root.dataset.phcNativeFacilitatorEmployeeId, ''));
      const name = text(first(profile?.name, root.dataset.phcNativeFacilitatorName, ''));
      if (profileId) root.dataset.phcNativeFacilitatorProfileId = profileId;
      if (employeeId) root.dataset.phcNativeFacilitatorEmployeeId = employeeId;
      if (name) root.dataset.phcNativeFacilitatorName = name;
      if (profileId) rememberFacilitatorIdentity({ publicProfileId: profileId, employeeId, name });
      if (profileId) finish(profileId);
    };
    const handler = (event) => apply(event?.detail || {});
    window.addEventListener('phc:facilitator-loaded', handler);
    apply();
    if (settled) return;
    interval = window.setInterval(() => apply(), 120);
    timer = window.setTimeout(() => finish(''), timeoutMs);
  });

  const resolveFacilitatorIdentity = async (root) => {
    const section = root.closest?.('[data-phc-mariana-facilitator-schedule]');
    const applyIdentity = (profileIdValue, employeeIdValue = '', nameValue = '') => {
      const profileId = text(profileIdValue);
      const employeeId = text(employeeIdValue);
      const name = text(nameValue);
      if (profileId) root.dataset.phcNativeFacilitatorProfileId = profileId;
      if (employeeId) root.dataset.phcNativeFacilitatorEmployeeId = employeeId;
      if (name) root.dataset.phcNativeFacilitatorName = name;
      if (section) {
        if (profileId) section.dataset.marianaResolvedProfileId = profileId;
        if (employeeId) section.dataset.marianaResolvedEmployeeId = employeeId;
      }
      if (profileId || employeeId) rememberFacilitatorIdentity({ publicProfileId: profileId, employeeId, name });
      return profileId;
    };

    const resolvedProfileId = text(section?.dataset?.marianaResolvedProfileId);
    if (resolvedProfileId) return applyIdentity(resolvedProfileId, section?.dataset?.marianaResolvedEmployeeId, root.dataset.phcNativeFacilitatorName);

    const existingProfileId = text(root.dataset.phcNativeFacilitatorProfileId);
    if (existingProfileId) return applyIdentity(existingProfileId, root.dataset.phcNativeFacilitatorEmployeeId, root.dataset.phcNativeFacilitatorName);

    // When navigation originates from a session modal, the source session has
    // already resolved the Mariana person. Reuse that verified identity instead
    // of throwing it away and racing the profile loader on the destination page.
    const remembered = readRememberedFacilitatorIdentity();
    if (remembered?.publicProfileId) {
      return applyIdentity(remembered.publicProfileId, remembered.employeeId, remembered.name);
    }

    const employeeId = text(first(root.dataset.phcNativeFacilitatorEmployeeId, remembered?.employeeId, ''));
    const proxy = text(root.dataset.phcNativeProxyUrl);
    const eventIdentity = waitForFacilitatorIdentity(root, 7000);

    if (!employeeId || !proxy) return eventIdentity;

    const key = `${proxy}|facilitator|${employeeId}`;
    let detailPromise = cache.get(key);
    if (!detailPromise) {
      detailPromise = (async () => {
        const response = await fetch(buildProxyUrl(proxy, 'facilitator', { employee_id: employeeId }), {
          headers: { Accept: 'application/json' },
          credentials: 'omit'
        });
        const payload = await safeJson(response);
        if (!response.ok) throw new Error(payload?.error || `Facilitator lookup returned ${response.status}`);
        const profile = payload?.profile || {};
        const profileId = text(first(profile?.public_profile_id, profile?.publicProfileId, profile?.id, ''));
        if (!profileId) throw new Error('Facilitator lookup returned no public profile id');
        return {
          profileId,
          employeeId: text(first(profile?.employee_id, employeeId, '')),
          name: text(profile?.name)
        };
      })();
      cache.set(key, detailPromise);
    }

    try {
      const detail = await detailPromise;
      return applyIdentity(detail.profileId, detail.employeeId, detail.name);
    } catch (_) {
      cache.delete(key);
      // The profile loader is already resolving this same page by Shopify name.
      // If the targeted request fails transiently, let that verified hand-off
      // complete instead of showing an error that disappears after reload.
      const profileId = await eventIdentity;
      return profileId ? applyIdentity(profileId, root.dataset.phcNativeFacilitatorEmployeeId, root.dataset.phcNativeFacilitatorName) : '';
    }
  };

  const formatTime = (date) => date ? new Intl.DateTimeFormat('en-GB', { timeZone: PHC_TIME_ZONE, hour: '2-digit', minute: '2-digit', hour12: false }).format(date) : '';
  const formatDateLong = (date) => date ? new Intl.DateTimeFormat('en-GB', { timeZone: PHC_TIME_ZONE, weekday: 'long', day: 'numeric', month: 'long' }).format(date) : '';
  const formatDay = (date) => new Intl.DateTimeFormat('en-GB', { timeZone: PHC_TIME_ZONE, weekday: 'short' }).format(date);
  const formatMonthDay = (date) => new Intl.DateTimeFormat('en-GB', { timeZone: PHC_TIME_ZONE, day: 'numeric', month: 'short' }).format(date);
  const formatMonthShort = (date) => new Intl.DateTimeFormat('en-GB', { timeZone: PHC_TIME_ZONE, month: 'short' }).format(date).replace('.', '').slice(0, 3).toUpperCase();
  const normalizeIdentity = (value) => text(value)
    .normalize('NFKD').replace(/[\u0300-\u036f]/g, '')
    .toLowerCase().replace(/[^a-z0-9]+/g, '');
  const looksLikeOpaqueId = (value) => {
    const raw = text(value);
    if (!raw) return false;
    return /^\d+$/.test(raw) || /^[a-f0-9_-]{16,}$/i.test(raw);
  };
  const facilitatorIds = (person) => Array.from(new Set([
    text(person?.publicProfileId),
    text(person?.employeeId),
    text(person?.id)
  ].filter(Boolean)));
  // Booking calendar rule: once a session has started, it is no longer a
  // future booking option. Use start time (not end time) as the cutoff.
  const sessionCutoff = (session) => {
    if (session?.start instanceof Date && !Number.isNaN(session.start.getTime())) return session.start;
    return null;
  };
  const sessionIsFinished = (session, now = new Date()) => {
    const cutoff = sessionCutoff(session);
    return !cutoff || cutoff.getTime() <= now.getTime();
  };

  class NativeSchedule {
    constructor(root) {
      this.root = root;
      this.state = { sessions: [], filtered: [], selectedDate: '', weekOffset: 0, location: '', ritual: '', facilitator: '' };
      this.facilitatorDirectory = { byId: new Map(), byName: new Map(), profiles: [] };
      this.facilitatorPages = this.readFacilitatorPages();
      this.latestScheduleResponse = null;
      this.isLoadingSchedule = false;
      this.bookingObserver = null;
      this.expiryTimer = null;
      this.loadingMessageTimers = [];
      this.boundLoaded = (event) => this.onFacilitatorLoaded(event);
      window.addEventListener('phc:facilitator-loaded', this.boundLoaded);
      this.bind();
      this.openBookingFromUrl();
      this.load();
    }

    todayIso() {
      const serverDate = text(this.root.dataset.phcNativeToday);
      if (/^\d{4}-\d{2}-\d{2}$/.test(serverDate)) return serverDate;
      const browserDate = localIso(new Date());
      if (/^\d{4}-\d{2}-\d{2}$/.test(browserDate)) return browserDate;
      const now = new Date();
      return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    }

    readFacilitatorPages() {
      const node = qs(this.root, '[data-phc-native-facilitator-pages]');
      if (!node) return [];
      try {
        const rows = JSON.parse(node.textContent || '[]');
        return asArray(rows).map((page) => ({
          url: text(page?.url),
          title: text(page?.title),
          name: text(page?.name),
          handle: text(page?.handle),
          publicProfileId: text(page?.publicProfileId),
          employeeId: text(page?.employeeId)
        })).filter((page) => page.url);
      } catch (_) {
        return [];
      }
    }

    directoryProfileForPage(page) {
      if (!page) return null;
      const direct = [page.publicProfileId, page.employeeId]
        .map(text).filter(Boolean)
        .map((id) => this.facilitatorDirectory.byId.get(id))
        .find(Boolean);
      if (direct) return direct;

      for (const candidate of [page.name, page.title, page.handle]) {
        const key = normalizeIdentity(candidate);
        if (!key) continue;
        const resolved = this.facilitatorDirectory.byName.get(key);
        if (resolved) return resolved;
      }
      return null;
    }

    facilitatorPageFor(session, facilitator = null) {
      if (!this.facilitatorPages.length) return null;
      const ids = new Set();
      const names = new Set();
      const addId = (value) => { const normalized = text(value); if (normalized) ids.add(normalized); };
      const addName = (value) => { const normalized = normalizeIdentity(value); if (normalized) names.add(normalized); };

      addId(facilitator?.id);
      addId(facilitator?.publicProfileId);
      addId(facilitator?.employeeId);
      addName(facilitator?.name);
      asArray(facilitator?.aliases).forEach(addName);

      asArray(session?.instructors).forEach((person) => {
        facilitatorIds(person).forEach(addId);
        addName(person?.name);
        const personNameKey = normalizeIdentity(person?.name);
        const resolved = facilitatorIds(person).map((id) => this.facilitatorDirectory.byId.get(id)).find(Boolean)
          || (personNameKey ? this.facilitatorDirectory.byName.get(personNameKey) : null)
          || null;
        addId(resolved?.id);
        addId(resolved?.publicProfileId);
        addId(resolved?.employeeId);
        addName(resolved?.name);
        asArray(resolved?.aliases).forEach(addName);
      });

      const idMatches = this.facilitatorPages.filter((page) => {
        const resolvedPage = this.directoryProfileForPage(page);
        return [
          page.publicProfileId, page.employeeId,
          resolvedPage?.id, resolvedPage?.publicProfileId, resolvedPage?.employeeId
        ].map(text).filter(Boolean).some((id) => ids.has(id));
      });
      if (idMatches.length === 1) return idMatches[0];

      const nameMatches = this.facilitatorPages.filter((page) => {
        const resolvedPage = this.directoryProfileForPage(page);
        return [page.name, page.title, page.handle, resolvedPage?.name, ...asArray(resolvedPage?.aliases)]
          .map(normalizeIdentity).filter(Boolean).some((name) => names.has(name));
      });
      return nameMatches.length === 1 ? nameMatches[0] : null;
    }

    bind() {
      qsa(this.root, '[data-phc-native-filter]').forEach((select) => select.addEventListener('change', () => {
        const key = select.dataset.phcNativeFilter;
        this.state[key] = select.value;
        qsa(this.root, `[data-phc-native-filter="${key}"]`).forEach((mirror) => {
          if (mirror !== select) mirror.value = select.value;
        });
        this.state.weekOffset = 0;
        this.ensureUsefulDate(false);
        this.render();
      }));
      qsa(this.root, '[data-phc-native-reset]').forEach((button) => button.addEventListener('click', () => this.resetFilters()));
      qs(this.root, '[data-phc-native-mobile-open]')?.addEventListener('click', () => this.openMobileFilters());
      qs(this.root, '[data-phc-native-mobile-close]')?.addEventListener('click', () => this.closeMobileFilters());
      qs(this.root, '[data-phc-native-mobile-backdrop]')?.addEventListener('click', () => this.closeMobileFilters());
      qs(this.root, '[data-phc-native-mobile-apply]')?.addEventListener('click', () => this.closeMobileFilters());
      qs(this.root, '[data-phc-native-date-prev]')?.addEventListener('click', () => { this.state.weekOffset = Math.max(0, this.state.weekOffset - 7); this.renderDates(); });
      qs(this.root, '[data-phc-native-date-next]')?.addEventListener('click', () => { this.state.weekOffset += 7; this.renderDates(); });
      qs(this.root, '[data-phc-native-next-available]')?.addEventListener('click', () => this.jumpNextAvailable());
      qs(this.root, '[data-phc-native-retry]')?.addEventListener('click', () => this.load(true));
      this.root.addEventListener('click', (event) => {
        const link = event.target?.closest?.('[data-phc-facilitator-link]');
        if (!link) return;
        rememberFacilitatorIdentity({
          url: link.href,
          publicProfileId: link.dataset.phcProfileId,
          employeeId: link.dataset.phcEmployeeId,
          name: link.dataset.phcFacilitatorName
        });
      });
      const dialog = qs(this.root, '[data-phc-native-dialog]');
      qs(this.root, '[data-phc-native-dialog-close]')?.addEventListener('click', () => dialog?.close());
      dialog?.addEventListener('click', (event) => { if (event.target === dialog) dialog.close(); });
      dialog?.addEventListener('close', () => this.stopRitualVideo(dialog));

      const bookingDialog = qs(this.root, '[data-phc-native-booking-dialog]');
      qs(this.root, '[data-phc-native-booking-close]')?.addEventListener('click', () => this.closeBookingDialog());
      bookingDialog?.addEventListener('cancel', (event) => event.preventDefault());
      // Secure Mariana booking/auth is closed only by the explicit X. On mobile a
      // fullscreen <dialog> can report taps on empty shell space as the dialog
      // itself; treating that as a backdrop click caused accidental closes.
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.root.classList.contains('is-mobile-filter-open')) this.closeMobileFilters();
      });
    }

    async onFacilitatorLoaded(event) {
      if (this.root.dataset.phcNativeMode !== 'facilitator') return;
      const profile = event?.detail;
      const profileId = text(first(profile?.publicProfileId, profile?.id, ''));
      const employeeId = text(profile?.employeeId);
      const profileName = text(profile?.name);
      if (profileId) this.root.dataset.phcNativeFacilitatorProfileId = profileId;
      if (employeeId) this.root.dataset.phcNativeFacilitatorEmployeeId = employeeId;
      if (profileName) this.root.dataset.phcNativeFacilitatorName = profileName;
      if (profileId || employeeId) rememberFacilitatorIdentity({ publicProfileId: profileId, employeeId, name: profileName });
      // Identity enrichment only. The calendar owns one deterministic request
      // lifecycle and must never reload because the profile script finished.
    }

    stopLoadingMessages() {
      this.loadingMessageTimers.forEach((timer) => {
        window.clearTimeout(timer);
        window.clearInterval(timer);
      });
      this.loadingMessageTimers = [];
    }

    startLoadingMessages() {
      this.stopLoadingMessages();
      const node = qs(this.root, '[data-phc-native-loading-message]');
      if (!node) return;

      const sequence = [
        'THIS IS THE NEW NORMAL',
        'HEAT. MUSIC. BREATH.',
        'JUST HOLD ON, ALMOST THERE',
        'YOUR NEXT SESSION IS ALMOST READY'
      ];
      let index = 0;
      node.textContent = sequence[index];

      // Calm, continuous brand loop while the schedule is genuinely loading.
      // The interval is cancelled immediately by setLoading(false).
      const timer = window.setInterval(() => {
        if (!this.root.hasAttribute('aria-busy')) return;
        index = (index + 1) % sequence.length;
        node.textContent = sequence[index];
      }, 2800);
      this.loadingMessageTimers.push(timer);
    }

    setLoading(loading) {
      const loadingNode = qs(this.root, '[data-phc-native-loading]');
      const app = qs(this.root, '[data-phc-native-app]');
      const error = qs(this.root, '[data-phc-native-error]');
      if (loadingNode) {
        loadingNode.hidden = !loading;
        loadingNode.style.display = loading ? '' : 'none';
      }
      if (app) app.hidden = loading;
      if (error) error.hidden = true;
      this.root.toggleAttribute('aria-busy', loading);
      if (loading) this.startLoadingMessages();
      else this.stopLoadingMessages();
    }

    applyScheduleResponse(response, directories = {}, initial = false) {
      const raw = response.raw || { data: response.sessions, included: [] };
      const index = includedIndex(raw);
      const now = new Date();
      this.state.sessions = asArray(response.sessions)
        .map((resource) => normalizeSession(resource, raw, { index, ...directories }))
        .filter((session) => {
          if (!session.id || !session.start || Number.isNaN(session.start.getTime())) return false;
          // Mariana's min_date includes the whole Copenhagen calendar day. Remove
          // anything whose Copenhagen start instant has already passed before it
          // reaches the date rail or session list.
          return !sessionIsFinished(session, now);
        })
        .sort((a,b) => a.start - b.start);

      const facilitatorMode = this.root.dataset.phcNativeMode === 'facilitator';
      if (facilitatorMode) {
        // Mariana already scoped this response to the verified public profile.
        // Never apply a second client-side identity filter that could erase it.
        this.state.facilitator = '';
      } else {
        const configuredProfile = text(this.root.dataset.phcNativeFacilitatorProfileId);
        const configuredEmployee = text(this.root.dataset.phcNativeFacilitatorEmployeeId);
        if (configuredProfile || configuredEmployee) this.state.facilitator = configuredProfile || configuredEmployee;
      }
      const configuredLocation = text(this.root.dataset.phcNativeLocationId);
      if (configuredLocation) this.state.location = configuredLocation;

      this.populateFilters();
      this.ensureUsefulDate(initial);
      this.render();
    }

    async enrichSchedule() {
      const [types, locations, facilitators] = await Promise.all([
        fetchDirectory(this.root, 'class_session_types'),
        fetchDirectory(this.root, 'locations'),
        fetchFacilitators(this.root)
      ]);
      this.facilitatorDirectory = facilitators;
      if (this.latestScheduleResponse) this.applyScheduleResponse(this.latestScheduleResponse, { types, locations }, false);
    }

    async load(force = false) {
      if (this.isLoadingSchedule) return;
      this.isLoadingSchedule = true;
      if (force) {
        for (const key of Array.from(cache.keys())) if (key.startsWith(text(this.root.dataset.phcNativeProxyUrl))) cache.delete(key);
      }
      this.setLoading(true);
      try {
        const fullDays = Math.min(14, Math.max(7, Number(this.root.dataset.phcNativeDays || 14)));
        const facilitatorMode = this.root.dataset.phcNativeMode === 'facilitator';
        const initialDays = fullDays;

        if (facilitatorMode) {
          const profileId = await resolveFacilitatorIdentity(this.root);
          if (!profileId) throw new Error('Unable to resolve facilitator public profile id');
        }

        // Exactly one 14-day schedule request. Facilitator pages are already
        // scoped server-side; the main calendar remains unchanged.
        const response = await fetchSchedule(this.root, { days: initialDays });
        this.latestScheduleResponse = response;
        this.applyScheduleResponse(response, {}, true);

        const app = qs(this.root, '[data-phc-native-app]');
        if (app) app.hidden = false;
        this.setLoading(false);

        // Keep metadata/photo enrichment out of the critical first-paint path.
        deferNonCritical(() => {
          this.enrichSchedule().catch((error) => {
            console.info('[PHC] Mariana schedule enrichment skipped:', error?.message || error);
          });
        }, 500);

        // One 14-day schedule request only. No background schedule reload.
      } catch (error) {
        console.warn('[PHC] Native Mariana schedule unavailable:', error);
        const loading = qs(this.root, '[data-phc-native-loading]');
        const app = qs(this.root, '[data-phc-native-app]');
        const errorNode = qs(this.root, '[data-phc-native-error]');
        if (loading) { loading.hidden = true; loading.style.display = 'none'; }
        if (app) app.hidden = true;
        if (errorNode) errorNode.hidden = false;
        this.root.removeAttribute('aria-busy');
      } finally {
        this.isLoadingSchedule = false;
      }
    }


    pruneExpiredSessions() {
      const now = new Date();
      const before = this.state.sessions.length;
      this.state.sessions = this.state.sessions.filter((session) => !sessionIsFinished(session, now));
      return before !== this.state.sessions.length;
    }

    scheduleExpirySweep() {
      if (this.expiryTimer) {
        window.clearTimeout(this.expiryTimer);
        this.expiryTimer = null;
      this.loadingMessageTimers = [];
      }
      const now = Date.now();
      const nextCutoff = this.state.sessions
        .map((session) => sessionCutoff(session)?.getTime() || 0)
        .filter((time) => time > now)
        .sort((a,b) => a-b)[0];
      if (!nextCutoff) return;
      // Re-evaluate just after the nearest session starts. Cap the timeout so a
      // long-open tab also gets a regular freshness check.
      const delay = Math.max(1000, Math.min(nextCutoff - now + 750, 60000));
      this.expiryTimer = window.setTimeout(() => {
        const changed = this.pruneExpiredSessions();
        if (changed) {
          this.populateFilters();
          this.ensureUsefulDate(false);
          this.render();
        } else {
          this.scheduleExpirySweep();
        }
      }, delay);
    }

    facilitatorMatches(person, value) {
      const wanted = text(value);
      if (!wanted) return true;

      const wantedIds = new Set([wanted]);
      const wantedNames = new Set();
      if (!looksLikeOpaqueId(wanted)) wantedNames.add(normalizeIdentity(wanted));

      // A facilitator page can be configured with either Mariana employee ID or
      // employee_public_profile ID. Treat both as the same person identity.
      if (this.root.dataset.phcNativeMode === 'facilitator') {
        const profileId = text(this.root.dataset.phcNativeFacilitatorProfileId);
        const employeeId = text(this.root.dataset.phcNativeFacilitatorEmployeeId);
        const facilitatorName = text(this.root.dataset.phcNativeFacilitatorName);
        if (profileId) wantedIds.add(profileId);
        if (employeeId) wantedIds.add(employeeId);
        if (facilitatorName) wantedNames.add(normalizeIdentity(facilitatorName));
      }

      const ids = new Set(facilitatorIds(person));
      const names = new Set();
      const personName = text(person?.name);
      if (personName) names.add(normalizeIdentity(personName));

      const personNameKey = normalizeIdentity(personName);
      const resolved = Array.from(ids).map((id) => this.facilitatorDirectory.byId.get(id)).find(Boolean)
        || (personNameKey ? this.facilitatorDirectory.byName.get(personNameKey) : null)
        || null;
      if (resolved) {
        [resolved.id, resolved.publicProfileId, resolved.employeeId].map(text).filter(Boolean).forEach((id) => ids.add(id));
        [resolved.name, ...asArray(resolved.aliases)].map(normalizeIdentity).filter(Boolean).forEach((name) => names.add(name));
      }

      if (Array.from(wantedIds).some((id) => ids.has(id))) return true;
      return Array.from(wantedNames).filter(Boolean).some((name) => names.has(name));
    }

    populateFilters() {
      const sessions = this.state.sessions;
      const locations = new Map();
      const rituals = new Set();
      const facilitatorsByIdentity = new Map();

      sessions.forEach((session) => {
        const locationValue = text(session.locationId || session.location);
        const locationLabel = text(session.location);
        if (locationValue && locationLabel && !looksLikeOpaqueId(locationLabel)) locations.set(locationValue, locationLabel);
        if (session.ritual) rituals.add(session.ritual);

        session.instructors.forEach((person) => {
          const ids = facilitatorIds(person);
          const rawName = text(person?.name);
          const resolved = ids.map((id) => this.facilitatorDirectory.byId.get(id)).find(Boolean)
            || (rawName && this.facilitatorDirectory.byName.get(rawName.toLowerCase()))
            || null;
          const label = text(resolved?.name) || (!looksLikeOpaqueId(rawName) && !ids.includes(rawName) ? rawName : '');
          if (!label) return;
          const value = text(first(resolved?.id, resolved?.employeeId, person?.publicProfileId, person?.employeeId, person?.id, label));
          // The same Mariana person can appear in class_sessions once by public
          // profile id and elsewhere by employee id. Collapse both identities to
          // one visible facilitator option instead of showing the same name twice.
          const identityKey = resolved
            ? `resolved:${text(first(resolved.id, resolved.employeeId, label)).toLowerCase()}`
            : `name:${label.toLowerCase()}`;
          if (!facilitatorsByIdentity.has(identityKey)) facilitatorsByIdentity.set(identityKey, { value, label });
        });
      });

      const facilitatorOptions = new Map(Array.from(facilitatorsByIdentity.values()).map((entry) => [entry.value, entry.label]));
      this.fillFilter('location', locations, 'All locations', this.state.location);
      this.fillFilter('ritual', new Map(Array.from(rituals).sort().map((x) => [x,x])), 'All rituals', this.state.ritual);
      this.fillFilter('facilitator', facilitatorOptions, 'All facilitators', this.state.facilitator);

      const lockedLocation = Boolean(text(this.root.dataset.phcNativeLocationId));
      const lockedFacilitator = this.root.dataset.phcNativeMode === 'facilitator' && Boolean(text(this.root.dataset.phcNativeFacilitatorProfileId));
      qsa(this.root, '[data-phc-native-location-filter-wrap]').forEach((wrap) => { wrap.hidden = lockedLocation || locations.size <= 1; });
      qsa(this.root, '[data-phc-native-facilitator-filter-wrap]').forEach((wrap) => { wrap.hidden = lockedFacilitator || facilitatorOptions.size <= 1; });
      this.updateFilterUi();
    }

    fillFilter(key, options, placeholder, current) {
      const selects = qsa(this.root, `[data-phc-native-filter="${key}"]`);
      if (!selects.length) return;
      const hasCurrent = options.has(current);
      const locked = key === 'location'
        ? text(this.root.dataset.phcNativeLocationId)
        : (key === 'facilitator' ? text(this.root.dataset.phcNativeFacilitatorProfileId) : '');
      const preserveLocked = Boolean(locked && current === locked);
      selects.forEach((select) => {
        select.replaceChildren(new Option(placeholder, ''));
        Array.from(options.entries())
          .sort((a,b) => String(a[1]).localeCompare(String(b[1])))
          .forEach(([value,label]) => select.add(new Option(label, value)));
        select.value = hasCurrent ? current : '';
      });
      this.state[key] = hasCurrent || preserveLocked ? current : '';
    }

    updateFilterUi() {
      const baseLoc = text(this.root.dataset.phcNativeLocationId);
      const baseFac = text(this.root.dataset.phcNativeFacilitatorProfileId);
      const active = [
        this.state.location && this.state.location !== baseLoc,
        Boolean(this.state.ritual),
        this.state.facilitator && this.state.facilitator !== baseFac
      ].filter(Boolean).length;
      const count = qs(this.root, '[data-phc-native-active-count]');
      if (count) {
        count.hidden = active === 0;
        count.textContent = String(active);
      }
    }

    openMobileFilters() {
      const sheet = qs(this.root, '[data-phc-native-mobile-sheet]');
      const backdrop = qs(this.root, '[data-phc-native-mobile-backdrop]');
      if (!sheet || !backdrop) return;
      this.root.classList.add('is-mobile-filter-open');
      sheet.hidden = false;
      backdrop.hidden = false;
      qs(this.root, '[data-phc-native-mobile-open]')?.setAttribute('aria-expanded', 'true');
      sheet.setAttribute('role', 'dialog');
      sheet.setAttribute('aria-modal', 'true');
      document.documentElement.classList.add('phc-mobile-filter-open');
      document.body.classList.add('phc-mobile-filter-open');
      requestAnimationFrame(() => qs(this.root, '[data-phc-native-mobile-close]')?.focus({ preventScroll: true }));
    }

    closeMobileFilters() {
      const sheet = qs(this.root, '[data-phc-native-mobile-sheet]');
      const backdrop = qs(this.root, '[data-phc-native-mobile-backdrop]');
      if (!sheet || !backdrop) return;
      this.root.classList.remove('is-mobile-filter-open');
      sheet.hidden = true;
      backdrop.hidden = true;
      sheet.removeAttribute('role');
      sheet.removeAttribute('aria-modal');
      qs(this.root, '[data-phc-native-mobile-open]')?.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove('phc-mobile-filter-open');
      document.body.classList.remove('phc-mobile-filter-open');
    }

    resetFilters() {
      ['location','ritual','facilitator'].forEach((key) => {
        const locked = key === 'location' ? text(this.root.dataset.phcNativeLocationId) : (key === 'facilitator' ? text(this.root.dataset.phcNativeFacilitatorProfileId) : '');
        this.state[key] = locked;
        qsa(this.root, `[data-phc-native-filter="${key}"]`).forEach((select) => { select.value = locked || ''; });
      });
      this.state.weekOffset = 0;
      this.ensureUsefulDate(false);
      this.render();
    }

    applyFilters(includeDate = true) {
      return this.state.sessions.filter((session) => {
        if (this.state.location && (session.locationId || session.location) !== this.state.location) return false;
        if (this.state.ritual && session.ritual !== this.state.ritual) return false;
        if (this.state.facilitator && !session.instructors.some((person) => this.facilitatorMatches(person, this.state.facilitator))) return false;
        if (includeDate && this.state.selectedDate && session.date !== this.state.selectedDate) return false;
        return true;
      });
    }

    ensureUsefulDate(initial) {
      const today = dateFromIso(this.todayIso());
      const baseFiltered = this.applyFilters(false);
      const current = this.state.selectedDate || localIso(today);
      const currentStart = dateFromIso(current);
      const nextWeekStart = dateFromIso(shiftIsoDate(current, 7));
      const inWeek = baseFiltered.filter((session) => session.start >= currentStart && session.start < nextWeekStart);
      const facilitatorMode = this.root.dataset.phcNativeMode === 'facilitator';

      // On a facilitator profile, never land on an empty "today" with a text
      // link when that facilitator already has a future session. Go straight to
      // their first scheduled session instead.
      if (facilitatorMode && baseFiltered.length) {
        const currentHasSession = baseFiltered.some((session) => session.date === current);
        if (!currentHasSession) {
          this.state.selectedDate = baseFiltered[0].date;
          this.state.weekOffset = 0;
          return;
        }
      } else if (initial && !inWeek.length && baseFiltered.length) {
        this.state.selectedDate = baseFiltered[0].date;
        // The main booking calendar renders its complete configured 14-day rail
        // in one pass. Keep it anchored at today even when the first available
        // session is in week two so the user can still see every loaded day.
        this.state.weekOffset = 0;
        return;
      }
      this.state.selectedDate = current;
    }

    render() {
      this.pruneExpiredSessions();
      this.renderDates();
      this.renderSessions();
      this.updateFilterUi();
      this.scheduleExpirySweep();
    }

    renderDates() {
      const container = qs(this.root, '[data-phc-native-dates]');
      if (!container) return;
      const facilitatorMode = this.root.dataset.phcNativeMode === 'facilitator';
      const maxDays = Math.min(14, Math.max(7, Number(this.root.dataset.phcNativeDays || 14)));
      const visibleDays = Math.min(14, maxDays);
      const startIso = this.todayIso();
      const baseFiltered = this.applyFilters(false);
      const datesWithSessions = new Set(baseFiltered.map((session) => session.date));
      const fragment = document.createDocumentFragment();

      for (let index = 0; index < visibleDays; index += 1) {
        const iso = shiftIsoDate(startIso, index);
        const display = isoDisplayParts(iso);
        if (!display) continue;
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'phc-native-schedule__date';
        button.dataset.phcDate = iso;
        if (iso === this.state.selectedDate) button.setAttribute('aria-current', 'date');
        button.innerHTML = `<span class="phc-native-schedule__date-day">${htmlEscape(display.weekday)}</span><span class="phc-native-schedule__date-number">${display.day}</span><span class="phc-native-schedule__date-month">${htmlEscape(display.monthShort)}</span>${datesWithSessions.has(iso) ? '<span class="phc-native-schedule__date-dot"></span>' : ''}`;
        button.addEventListener('click', () => {
          this.state.selectedDate = iso;
          this.render();
        });
        fragment.appendChild(button);
      }

      container.replaceChildren(fragment);
      if (facilitatorMode) {
        window.requestAnimationFrame(() => {
          const selected = container.querySelector('[aria-current="date"]');
          if (!selected) return;
          container.scrollLeft = Math.max(0, selected.offsetLeft);
        });
      }
      const prev = qs(this.root, '[data-phc-native-date-prev]');
      if (prev) prev.disabled = this.state.weekOffset <= 0;
      const next = qs(this.root, '[data-phc-native-date-next]');
      if (prev) { prev.disabled = true; prev.hidden = true; }
      if (next) { next.disabled = true; next.hidden = true; }
    }

    renderSessions() {
      const sessionsNode = qs(this.root, '[data-phc-native-sessions]');
      const empty = qs(this.root, '[data-phc-native-empty]');
      const summary = qs(this.root, '[data-phc-native-summary]');
      const sessions = this.applyFilters(true);
      sessionsNode.replaceChildren();
      if (summary) {
        const date = this.state.selectedDate ? dateFromIso(this.state.selectedDate, 12) : null;
        summary.textContent = date ? `${formatDateLong(date)} · ${sessions.length} session${sessions.length === 1 ? '' : 's'}` : '';
      }
      sessions.forEach((session) => sessionsNode.appendChild(this.sessionRow(session)));
      const facilitatorMode = this.root.dataset.phcNativeMode === 'facilitator';
      const noFacilitatorSessions = facilitatorMode && this.applyFilters(false).length === 0;
      this.root.classList.toggle('is-facilitator-empty', noFacilitatorSessions);
      if (empty) empty.hidden = noFacilitatorSessions ? false : sessions.length > 0;
      const nextButton = qs(this.root, '[data-phc-native-next-available]');
      if (nextButton) nextButton.hidden = facilitatorMode || noFacilitatorSessions || !this.findNextAvailable();
      const bookSessionsLink = qs(this.root, '[data-phc-native-empty-book]');
      if (bookSessionsLink) bookSessionsLink.hidden = !noFacilitatorSessions;
      const emptyCopy = qs(this.root, '[data-phc-native-empty-copy]');
      if (emptyCopy && noFacilitatorSessions) emptyCopy.textContent = 'NO SESSIONS ARE SCHEDULED AT THE MOMENT.';
      else if (emptyCopy) emptyCopy.textContent = 'No sessions are scheduled for this day.';
    }

    ritualImage(session) {
      const ritual = text(session?.ritual).toLowerCase();
      if (ritual === 'deep') return text(this.root.dataset.phcNativeImageDeep);
      if (ritual === 'club') return text(this.root.dataset.phcNativeImageClub);
      if (ritual === 'peak') return text(this.root.dataset.phcNativeImagePeak);
      return text(this.root.dataset.phcNativeImageFallback);
    }

    ritualVideoId(session) {
      const ritual = text(session?.ritual).toLowerCase();
      if (ritual === 'deep') return text(this.root.dataset.phcNativeVideoDeep);
      if (ritual === 'club') return text(this.root.dataset.phcNativeVideoClub);
      if (ritual === 'peak') return text(this.root.dataset.phcNativeVideoPeak);
      return '';
    }

    warmRitualVideoConnection() {
      ['https://www.youtube.com', 'https://i.ytimg.com'].forEach((href) => {
        if (document.head.querySelector(`link[rel="preconnect"][href="${href}"]`)) return;
        const link = document.createElement('link');
        link.rel = 'preconnect';
        link.href = href;
        document.head.appendChild(link);
      });
    }

    stopRitualVideo(scope) {
      const iframe = scope?.querySelector?.('.phc-native-session__video');
      if (!iframe) return;
      // Removing the iframe is the most reliable cross-origin way to stop audio
      // immediately without depending on the YouTube Player API.
      try { iframe.src = 'about:blank'; } catch (_) {}
      iframe.remove();
    }

    playRitualVideo(container, videoId, title) {
      if (!container || !videoId) return;
      this.warmRitualVideoConnection();
      const iframe = document.createElement('iframe');
      iframe.className = 'phc-native-session__video';
      iframe.title = `${title || 'Plugin Heat Club'} video`;
      iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
      iframe.allowFullscreen = true;
      iframe.loading = 'eager';
      iframe.referrerPolicy = 'strict-origin-when-cross-origin';
      iframe.src = `https://www.youtube.com/embed/${encodeURIComponent(videoId)}?autoplay=1&rel=0&modestbranding=1&playsinline=1`;
      container.replaceChildren(iframe);
    }

    facilitatorForPerson(person) {
      if (!person) return null;
      const name = text(person?.name);
      const nameKey = normalizeIdentity(name);
      const resolved = facilitatorIds(person).map((id) => this.facilitatorDirectory.byId.get(id)).find(Boolean)
        || (nameKey ? this.facilitatorDirectory.byName.get(nameKey) : null)
        || null;
      if (resolved) return resolved;
      return {
        id: text(first(person.publicProfileId, person.employeeId, person.id, '')),
        publicProfileId: text(person.publicProfileId),
        employeeId: text(person.employeeId),
        name,
        aliases: name ? [name] : [],
        image: ''
      };
    }

    facilitatorForSession(session) {
      for (const person of asArray(session?.instructors)) {
        const resolved = this.facilitatorForPerson(person);
        if (resolved) return resolved;
      }
      return null;
    }

    facilitatorsForSession(session) {
      const facilitatorMode = this.root.dataset.phcNativeMode === 'facilitator';
      const seen = new Set();
      const entries = [];
      asArray(session?.instructors).forEach((person) => {
        const facilitator = this.facilitatorForPerson(person);
        if (!facilitator) return;
        const name = text(first(facilitator?.name, person?.name, ''));
        const identity = text(first(facilitator?.id, facilitator?.publicProfileId, facilitator?.employeeId, person?.publicProfileId, person?.employeeId, person?.id, normalizeIdentity(name)));
        if (!identity || seen.has(identity)) return;
        seen.add(identity);
        const page = facilitatorMode ? null : this.facilitatorPageFor({ instructors: [person] }, facilitator);
        entries.push({ facilitator, page, name });
      });
      return entries;
    }

    sessionRow(session) {
      const row = document.createElement('article');
      row.className = 'phc-native-session-row';
      const facilitator = this.facilitatorForSession(session);
      const image = this.ritualImage(session);
      const bookLabel = session.availabilityState === 'waitlist' ? 'JOIN WAITLIST' : 'BOOK';
      const canOpenBooking = session.availabilityState !== 'cancelled' && session.availabilityState !== 'full';
      const timeRange = `${formatTime(session.start)}${session.end ? `–${formatTime(session.end)}` : ''}`;
      const locationMeta = [session.location, session.classroom].filter(Boolean).join(' · ');
      row.innerHTML = `
        <button type="button" class="phc-native-session-row__details" aria-label="View ${htmlEscape(session.ritual || session.name)} details">
          <span class="phc-native-session-row__media"><img src="${htmlEscape(image)}" alt="" loading="lazy"></span>
          <span class="phc-native-session-row__time">${htmlEscape(timeRange)}</span>
          <span class="phc-native-session-row__identity">
            <strong>${htmlEscape(session.ritual || session.name)}</strong>
            ${locationMeta ? `<small>${htmlEscape(locationMeta)}</small>` : ''}
          </span>
          <span class="phc-native-session-row__facilitator">
            ${facilitator?.image ? `<img src="${htmlEscape(facilitator.image)}" alt="" loading="lazy">` : ''}
            <span>${htmlEscape(facilitator?.name || session.instructors.map((x) => x.name).filter(Boolean).join(', '))}</span>
          </span>
          <span class="phc-native-session-row__availability" data-state="${htmlEscape(session.availabilityState)}">${htmlEscape(session.availabilityLabel)}</span>
        </button>
        ${canOpenBooking ? `<button type="button" class="phc-button phc-native-session-row__book" data-phc-book>${bookLabel}</button>` : `<span class="phc-native-session-row__book phc-native-session-row__book--disabled">${session.availabilityState === 'full' ? 'FULL' : 'CANCELLED'}</span>`}`;
      row.querySelector('.phc-native-session-row__details')?.addEventListener('click', () => this.openSession(session));
      row.querySelector('[data-phc-book]')?.addEventListener('click', () => this.openBooking(session));
      return row;
    }

    findNextAvailable() {
      if (!this.state.selectedDate) return null;
      const selected = dateFromIso(this.state.selectedDate, 23, 59, 59);
      return this.applyFilters(false).find((session) => session.start > selected) || null;
    }

    jumpNextAvailable() {
      const next = this.findNextAvailable();
      if (!next) return;
      this.state.selectedDate = next.date;
      const today = dateFromIso(this.todayIso());
      const target = dateFromIso(next.date);
      this.state.weekOffset = Math.max(0, Math.floor((target - today) / DAY_MS / 7) * 7);
      this.render();
    }

    bookingUrl(session) {
      const url = new URL(window.location.href);
      url.searchParams.set('_mt', `/classes/${session.id}/reserve`);
      url.searchParams.set('phc_booking', '1');
      return url.toString();
    }

    bookingContext() {
      try {
        const context = JSON.parse(sessionStorage.getItem('phc:booking-context') || 'null');
        if (!context || typeof context !== 'object') return null;
        const createdAt = Number(context.createdAt || 0);
        if (!createdAt || Date.now() - createdAt > 2 * 60 * 60 * 1000) return null;
        if (text(context.pathname) && text(context.pathname) !== window.location.pathname) return null;
        return context;
      } catch (_) {
        return null;
      }
    }

    openBooking(session) {
      if (!session?.id || session.availabilityState === 'cancelled' || session.availabilityState === 'full') return;
      const route = `/classes/${session.id}/reserve`;
      try {
        sessionStorage.setItem('phc:booking-context', JSON.stringify({
          sessionId: text(session.id),
          route,
          pathname: window.location.pathname,
          createdAt: Date.now(),
          title: session.ritual || session.name || 'Session',
          meta: [formatDateLong(session.start), `${formatTime(session.start)}${session.end ? `–${formatTime(session.end)}` : ''}`, session.location].filter(Boolean).join(' · ')
        }));
      } catch (_) {}
      window.location.assign(this.bookingUrl(session));
    }

    applyBookingContext() {
      const context = this.bookingContext();
      const title = qs(this.root, '[data-phc-native-booking-title]');
      const meta = qs(this.root, '[data-phc-native-booking-meta]');
      if (title && context?.title) title.textContent = context.title;
      if (meta && context?.meta) meta.textContent = context.meta;
    }

    watchBookingIntegration() {
      const integration = qs(this.root, '[data-phc-native-booking-integration]');
      const loading = qs(this.root, '[data-phc-native-booking-loading]');
      if (!integration || !loading) return;
      loading.hidden = false;
      const ready = () => {
        const hasFrame = Boolean(integration.querySelector('iframe'));
        const hasContent = integration.children.length > 0 || text(integration.textContent).length > 24;
        if (!hasFrame && !hasContent) return false;
        loading.hidden = true;
        this.bookingObserver?.disconnect();
        this.bookingObserver = null;
        return true;
      };
      if (ready()) return;
      this.bookingObserver?.disconnect();
      this.bookingObserver = new MutationObserver(() => ready());
      this.bookingObserver.observe(integration, { childList: true, subtree: true, characterData: true });
    }

    openBookingFromUrl() {
      const url = new URL(window.location.href);
      const route = text(url.searchParams.get('_mt'));
      const isReserveRoute = /^\/classes\/[^/]+\/reserve(?:[/?#]|$)/.test(route);
      const isCallbackRoute = /^\/callback(?:[/?#]|$)/.test(route);
      const context = this.bookingContext();
      const callbackBelongsToBooking = isCallbackRoute && (
        url.searchParams.get('phc_booking') === '1' ||
        /^\/classes\/[^/]+\/reserve(?:[/?#]|$)/.test(text(context?.route))
      );

      // Mariana returns customer login to this Shopify page with `_mt=/callback?...`.
      // That callback route MUST stay mounted in the same native Web Integration
      // host until Mariana consumes code/state and resumes the original reserve
      // route. Closing the PHC dialog or refusing to start the integration here
      // makes a valid Mariana session look like a logout.
      if (!isReserveRoute && !callbackBelongsToBooking) return;
      const dialog = qs(this.root, '[data-phc-native-booking-dialog]');
      if (!dialog) return;
      this.applyBookingContext();
      try { if (!dialog.open) dialog.showModal(); } catch (_) {}
      const bookingBody = dialog.querySelector('.phc-native-booking-modal__body');
      if (bookingBody) bookingBody.scrollTop = 0;
      this.watchBookingIntegration();
    }

    closeBookingDialog() {
      this.bookingObserver?.disconnect();
      this.bookingObserver = null;
      const dialog = qs(this.root, '[data-phc-native-booking-dialog]');
      try { dialog?.close(); } catch (_) {}
      const url = new URL(window.location.href);
      url.searchParams.delete('_mt');
      url.searchParams.delete('phc_booking');
      try { sessionStorage.removeItem('phc:booking-context'); } catch (_) {}
      window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    }

    openSession(session) {
      const dialog = qs(this.root, '[data-phc-native-dialog]');
      const content = qs(this.root, '[data-phc-native-dialog-content]');
      if (!dialog || !content) return;
      const facilitatorMode = this.root.dataset.phcNativeMode === 'facilitator';
      const facilitatorEntries = this.facilitatorsForSession(session);
      const instructor = session.instructors.map((x) => x.name).filter(Boolean).join(', ');
      const facilitatorMarkup = facilitatorEntries.map(({ facilitator, page, name }) => {
        const label = text(first(name, page?.name, facilitator?.name, ''));
        if (!label) return '';
        const imageMarkup = facilitator?.image ? `<img src="${htmlEscape(facilitator.image)}" alt="" loading="lazy">` : '';
        if (page?.url) {
          const profileId = text(first(facilitator?.publicProfileId, facilitator?.id, page?.publicProfileId, ''));
          const employeeId = text(first(facilitator?.employeeId, page?.employeeId, ''));
          return `<a class="phc-native-session__facilitator phc-native-session__facilitator--link" href="${htmlEscape(page.url)}" data-phc-facilitator-link data-phc-profile-id="${htmlEscape(profileId)}" data-phc-employee-id="${htmlEscape(employeeId)}" data-phc-facilitator-name="${htmlEscape(label)}">${imageMarkup}<span><small>FACILITATOR</small><strong>${htmlEscape(label)}</strong><em>VIEW PROFILE</em></span><b aria-hidden="true">→</b></a>`;
        }
        return `<div class="phc-native-session__facilitator">${imageMarkup}<span><small>FACILITATOR</small><strong>${htmlEscape(label)}</strong></span></div>`;
      }).join('');
      const duration = session.start && session.end ? Math.max(0, Math.round((session.end - session.start) / 60000)) : 0;
      const image = this.ritualImage(session);
      const ritualVideoId = this.ritualVideoId(session);
      if (ritualVideoId) this.warmRitualVideoConnection();
      const bookLabel = session.availabilityState === 'waitlist' ? 'JOIN WAITLIST' : 'BOOK THIS SESSION';
      const canBook = session.availabilityState !== 'cancelled' && session.availabilityState !== 'full';
      content.innerHTML = `
        <div class="phc-native-session__hero" data-phc-dialog-media>
          ${ritualVideoId
            ? `<button type="button" class="phc-native-session__video-trigger" data-phc-dialog-video aria-label="Play ${htmlEscape(session.ritual || session.name)} video"><img src="${htmlEscape(image)}" alt="" loading="eager"><span class="phc-ritual-card__play" aria-hidden="true"><span></span></span></button>`
            : `<img src="${htmlEscape(image)}" alt="" loading="eager">`}
        </div>
        <div class="phc-native-session__panel">
          <div class="phc-native-session__inner">
          <p class="phc-native-session__kicker">${htmlEscape(session.location || 'Plugin Heat Club')}</p>
          <h3 class="phc-native-session__title">${htmlEscape(session.ritual || session.name)}</h3>
          ${facilitatorMarkup}
          <div class="phc-native-session__facts">
            <div class="phc-native-session__fact"><span>Date</span><strong>${htmlEscape(formatDateLong(session.start))}</strong></div>
            <div class="phc-native-session__fact"><span>Time</span><strong>${htmlEscape(formatTime(session.start))}${session.end ? `–${htmlEscape(formatTime(session.end))}` : ''}</strong></div>
            ${instructor && facilitatorEntries.length === 0 ? `<div class="phc-native-session__fact"><span>Facilitator</span><strong>${htmlEscape(instructor)}</strong></div>` : ''}
            ${duration ? `<div class="phc-native-session__fact"><span>Duration</span><strong>${duration} min</strong></div>` : ''}
            <div class="phc-native-session__fact"><span>Availability</span><strong>${htmlEscape(session.availabilityLabel)}</strong></div>
            ${session.classroom ? `<div class="phc-native-session__fact"><span>Room</span><strong>${htmlEscape(session.classroom)}</strong></div>` : ''}
          </div>
          ${session.description ? `<div class="phc-native-session__description">${htmlEscape(session.description)}</div>` : ''}
          ${!canBook ? `<p class="phc-native-session__unavailable">${session.availabilityState === 'full' ? 'This session is currently full.' : 'This session has been cancelled.'}</p>` : ''}
          </div>
          ${canBook ? `<div class="phc-native-session__cta"><button class="phc-button phc-native-session__book" type="button" data-phc-dialog-book>${bookLabel}</button></div>` : ''}
        </div>`;
      content.querySelector('[data-phc-dialog-video]')?.addEventListener('click', () => {
        this.playRitualVideo(content.querySelector('[data-phc-dialog-media]'), ritualVideoId, session.ritual || session.name);
      });
      content.querySelector('[data-phc-dialog-book]')?.addEventListener('click', () => this.openBooking(session));
      dialog.showModal();
    }

  }

  const boot = (scope = document) => {
    const roots = scope.matches?.(ROOT) ? [scope] : qsa(scope, ROOT);
    roots.forEach((root) => {
      if (root.dataset.phcNativeReady === 'true') return;
      root.dataset.phcNativeReady = 'true';
      new NativeSchedule(root);
    });
  };

  window.PHCNativeSchedule = { boot };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot()); else boot();
  document.addEventListener('shopify:section:load', (event) => boot(event.target));
})();
